import { useModalDarkContext } from "@/components/context/ModalDarkContext"
import { globalFetchPolicy } from "@/components/layout/fetchPolicies"
import { useCheckDeliveryConfirmationMutation, useOrgWebsiteQuery, useSendAssistanceRequestMutation } from "@/graphql"
import { parseDeliveryLinkParams } from "@/helpers/deliveryLink"
import withScrollToTop from "@/hoc/withScrollToTop"
import { useBreakpoint } from "@/hooks/useBreakpoint"
import Footer from "@/pages/website-landing/components/footer/Footer"
import BenefitsSection from "@/pages/website-landing/home/new-sections/benefits-section/BenefitsSection"
import LogoSection from "@/pages/website-landing/home/new-sections/logo-section/LogoSection"
import ProblemSolutionSection from "@/pages/website-landing/home/new-sections/problem-solution-section/ProblemSolutionSection"
import StartGuideSteps from "@/pages/website-landing/home/new-sections/start-guide-steps/StartGuideSteps"
import WhatMatters from "@/pages/website-landing/home/new-sections/what-matters/WhatMatters"
import CustomerReviews from "@/pages/website-landing/home/sections/customer-reviews/CustomerReviews"
import EasetechHomeHero from "@/pages/website-landing/home/sections/easetech-home-hero/EasetechHomeHero"
import Industries from "@/pages/website-landing/home/sections/industries/Industries"
import InfoContact from "@/pages/website-landing/home/sections/info-contact/InfoContact"
import WeOffer from "@/pages/website-landing/home/sections/we-offer/WeOffer"
import OrgHowItWorks from "@/pages/website-landing/org-home/org-how-it-works/OrgHowItWorks"
import OrgLanding from "@/pages/website-landing/org-home/org-landing/OrgLanding"
import { EASETECH } from "@/utilities/global-variables"
import { FC, useEffect, useMemo, useRef } from "react"
import { useParams } from "react-router"
import { useSearchParams } from "react-router-dom"
import PartnersNet, { PartnerItem } from "../channel-partners/partners-net/PartnersNet"
import HeroSection from "./new-sections/hero-section/HeroSection"

// import AboutUs from '@/pages/website-landing/home/sections/about-us/AboutUs'
// import About from '@/pages/website-landing/home/sections/about/About'
// import Benefits from '@/pages/website-landing/home/sections/benefits/Benefits'
// import Footer from '@/pages/website-landing/components/footer/Footer'
// import HowToApply from '@/pages/website-landing/home/sections/how-to-apply/HowToApply'
// import OurServices from '@/pages/website-landing/home/sections/our-services/OurServices'
// import Planning from '@/pages/website-landing/home/sections/planning/Planning'
// import ReferUs from '@/pages/website-landing/home/sections/refer-us/ReferUs'
// import UtilityServices from '@/pages/website-landing/home/sections/utility-services/UtilityServices'

const Home: FC = () => {
  const [checkDeliveryConfirmation] = useCheckDeliveryConfirmationMutation()

  const params = useParams()
  const [searchParams] = useSearchParams()
  const currentOrgSlug = params.org || EASETECH
  const { openDarkModal } = useModalDarkContext()
  const { xxl, xl, lg, md, sm } = useBreakpoint()

  const isDelivery = searchParams.get("delivery-confirmation") === "true"

  const deliveryData = useMemo(() => parseDeliveryLinkParams(searchParams), [searchParams])
  const primaryApprovalUid = deliveryData.items[0]?.approvalUid
  const processedDeliveryLinkRef = useRef<string | null>(null)
  const deliveryLinkKey = isDelivery ? searchParams.toString() : null

  // ===> OrgHome data (other organizations' home pages; Easetech's own home page has its content in the code)

  const { data: orgData, loading: orgLoading } = useOrgWebsiteQuery({
    fetchPolicy: globalFetchPolicy,
    skip: currentOrgSlug === EASETECH,
  })
  const orgWebsiteContent = orgData?.orgWebsite?.data?.attributes?.orgHomePage?.[0] as ComponentPageOrgHome
  const bgUrl = orgWebsiteContent?.hero?.mainBackground?.data?.attributes?.url

  useEffect(() => {
    if (!deliveryLinkKey || !primaryApprovalUid) return
    if (processedDeliveryLinkRef.current === deliveryLinkKey) return

    processedDeliveryLinkRef.current = deliveryLinkKey

    const runCheck = async () => {
      try {
        const { data } = await checkDeliveryConfirmation({
          variables: {
            input: {
              uuid: primaryApprovalUid,
            },
          },
        })

        const isConfirmed = data?.checkDeliveryConfirmation?.result?.isDeliveryConfirmed

        ;(window as any).__DELIVERY_DATA__ = deliveryData

        openDarkModal({
          type: isConfirmed ? "delivery-confirmed" : "delivery-confirmation",
          mode: isConfirmed ? "default" : "soft",
          width: isConfirmed ? "width900" : "width1200",
        })
      } catch (e) {
        console.error("Delivery check failed", e)
        ;(window as any).__DELIVERY_DATA__ = deliveryData

        openDarkModal({
          type: "delivery-confirmation",
          mode: "soft",
          width: "width1200",
        })
      }
    }

    runCheck()
  }, [deliveryLinkKey, primaryApprovalUid, checkDeliveryConfirmation, openDarkModal, deliveryData])

  // useEffect(() => {
  //   if (isDelivery) {
  //     openDarkModal({
  //       type: 'delivery-confirmation',
  //       mode: 'soft',
  //       width: 'width1200',
  //     });
  //
  //     (window as any).__DELIVERY_DATA__ = {
  //       company,
  //       application,
  //       approvalUid,
  //     };
  //   }
  // }, [isDelivery]);

  // ===>> Hexagons texts

  const hexItems = orgWebsiteContent?.orgHowItWorks?.hexItems?.map(item => item?.value ?? "") ?? []

  // ==> media for PartnersNet

  const partnerItems: PartnerItem[] =
    orgWebsiteContent?.partners?.pictures?.data
      ?.map(file => {
        const url = file.attributes?.url

        if (!url) return null

        return {
          id: file.id,
          url,
        }
      })
      .filter((item): item is PartnerItem => Boolean(item)) ?? []

  // ===> Referral link (temporary disabled)

  // useEffect(() => {
  //   const rfScript = document.createElement('script')
  //   rfScript.id = 'rf-script'
  //   rfScript.src = referralWidgetScriptSrc
  //   if(currentOrgSlug === EASETECH)document.body.appendChild(rfScript)
  //
  //   return () => {
  //     if(currentOrgSlug === EASETECH) {
  //       const btnElement = document.getElementById('rf-launcher')
  //       btnElement?.remove()
  //       document.body.removeChild(rfScript)
  //     }
  //   }
  // }, [currentOrgSlug])

  return currentOrgSlug === EASETECH ? (
    <>
      <HeroSection />
      <WhatMatters />
      <BenefitsSection />
      <ProblemSolutionSection />
      <LogoSection />
      <StartGuideSteps />
      <CustomerReviews />
      <InfoContact isBannerVisible={false} paddingTop={xxl || xl || lg || md || sm ? 130 : 60} />

      {/* ======> 🗝️ old-designed components*/}
      {/*<EasetechHomeHero content={homePageContent?.hero as ComponentUiCard} loading={loading} />*/}
      {/*<Industries />*/}
      {/*<WeOffer />*/}
      {/*<Benefits*/}
      {/*  title={homePageContent?.benefitsHeadline as ComponentUiHeadline}*/}
      {/*  content={homePageContent?.benefits as ComponentUiHeadline[]}*/}
      {/*  loading={loading}*/}
      {/*/>*/}
      {/*<AboutUs />*/}
      {/*<OurServices />*/}
      {/*<UtilityServices />*/}
      {/*<HowToApply />*/}
      {/*<ReferUs />*/}
      {/*<Planning />*/}
    </>
  ) : (
    <>
      {/*<OrgHomeHero />*/}
      <OrgLanding
        content={{
          title: orgWebsiteContent?.hero?.title || "",
          subtitle: orgWebsiteContent?.hero?.subtitle || "",
          bgImage: bgUrl,
        }}
      />
      <OrgHowItWorks
        content={{
          title: orgWebsiteContent?.orgHowItWorks?.titleBlock?.title || "",
          subtitle: orgWebsiteContent?.orgHowItWorks?.titleBlock?.subtitle || "",
          hexItem: hexItems || [],
          bannerText: orgWebsiteContent?.orgHowItWorks?.bannerText || "",
        }}
      />

      {!orgLoading && partnerItems.length > 0 && (
        <PartnersNet
          isButton={true}
          title={{
            bold: orgWebsiteContent?.partners?.titleBlock?.title || "",
            regular: orgWebsiteContent?.partners?.titleBlock?.subtitle || "",
          }}
          loading={orgLoading}
          items={partnerItems}
        />
      )}

      <Footer isOrgFooter={true} />
    </>
  )
}

export default withScrollToTop(Home)
